/**
 * Fuso horário do dashboard. O único módulo que sabe que fusos existem.
 *
 * **Hora de Lisboa** — a hora da região onde as boias estão fundeadas
 * (requisito do João, 14 ago 2026: o ecrã mostra a hora do sítio, não UTC).
 * A base de dados, a API e os logs dos nós continuam em UTC; a conversão
 * acontece toda neste módulo, nos dois sentidos.
 *
 * O requisito anterior (David, 13 ago 2026) era UTC fixo, para que o instante
 * escrito no seletor fosse o instante na base sem contas de cabeça. Essa conta
 * passa a ser feita por `doCampo`/`paraCampo` — nunca por quem escreve — e a
 * regra que fica de lá é a mesma: o fuso vai **escrito** no painel e no
 * seletor ("hora de Lisboa (UTC+1)" no verão), em vez de implícito.
 *
 * O fuso é fixo, não é o do browser — o dashboard é público, e um parceiro a
 * abri-lo noutro país veria outras horas sem nada que lho dissesse.
 *
 * Voltar a UTC é trocar as duas constantes abaixo por `"UTC"` e `"UTC"`.
 * Mais nada muda.
 */

export const FUSO = "Europe/Lisbon";
export const ROTULO_FUSO = "hora de Lisboa";

/** `Intl` é caro de construir; um formatador por conjunto de opções, e chega. */
const cache = new Map<string, Intl.DateTimeFormat>();

export function formatador(opcoes: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const chave = JSON.stringify(opcoes);
  let f = cache.get(chave);
  if (f === undefined) {
    f = new Intl.DateTimeFormat("pt-PT", { ...opcoes, timeZone: FUSO });
    cache.set(chave, f);
  }
  return f;
}

// ---------------------------------------------------------------------------
// Campos <input type="datetime-local">
// ---------------------------------------------------------------------------

const CAMPO: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

function partes(d: Date, fuso: string): Record<string, string> {
  const f = new Intl.DateTimeFormat("en-CA", { ...CAMPO, timeZone: fuso });
  const r: Record<string, string> = {};
  for (const p of f.formatToParts(d)) r[p.type] = p.value;
  return r;
}

/**
 * Instante → `AAAA-MM-DDTHH:mm` no relógio de parede de Lisboa.
 *
 * Não dá para usar `getFullYear()`/`getHours()`: essas leem o fuso do browser,
 * que é precisamente o que se quer deixar de usar. E `toISOString()` dava UTC,
 * que era uma hora ao lado do que os gráficos mostram.
 */
export function paraCampo(d: Date, fuso: string = FUSO): string {
  const p = partes(d, fuso);
  // `hour12: false` produz "24" à meia-noite em vez de "00" nalguns motores.
  const hora = p["hour"] === "24" ? "00" : p["hour"];
  return `${p["year"]}-${p["month"]}-${p["day"]}T${hora}:${p["minute"]}`;
}

/** Desvio do fuso em relação a UTC, em ms, **naquele instante**. */
function desvio(d: Date, fuso: string): number {
  const bruto = Date.parse(`${paraCampo(d, fuso)}:00Z`) - d.getTime();
  // `paraCampo` trunca aos minutos, portanto até 59 s do instante vinham
  // dentro do desvio — era o "UTC−0.0106" no cabeçalho. Fusos reais são
  // múltiplos de 15 min; arredondar a isso recupera o desvio exato.
  return Math.round(bruto / 900_000) * 900_000;
}

/**
 * O desvio por extenso — "UTC+1" no verão, "UTC" no inverno.
 *
 * Leva o instante em causa em vez de assumir "agora": quem olha para uma
 * janela de inverno no verão veria um "+1" que não se aplica àqueles dados.
 */
export function desvioUtc(d: Date = new Date(), fuso: string = FUSO): string {
  const horas = desvio(d, fuso) / 3_600_000;
  if (horas === 0) return "UTC";
  return `UTC${horas > 0 ? "+" : "−"}${Math.abs(horas)}`;
}

/**
 * Como se anuncia o fuso no ecrã: "UTC", ou "hora de Lisboa (UTC+1)".
 *
 * O desvio só entra quando acrescenta alguma coisa — em UTC daria "UTC (UTC)".
 */
export function rotuloFuso(d: Date = new Date()): string {
  const d0 = desvioUtc(d);
  return d0 === ROTULO_FUSO ? ROTULO_FUSO : `${ROTULO_FUSO} (${d0})`;
}

/**
 * `AAAA-MM-DDTHH:mm` de Lisboa → o instante correspondente.
 *
 * Lê-se o texto como se fosse UTC e subtrai-se o desvio real. A segunda
 * passagem não é zelo a mais: nos dois domingos de mudança de hora o desvio
 * antes e depois da correção não é o mesmo, e uma passagem só erra 60 minutos.
 *
 * Devolve `null` se o texto não for exatamente este formato — o `<input>`
 * aceita anos com mais de 4 dígitos, e quem escreve depressa no segmento do ano
 * acaba com `202619` sem dar por isso.
 */
export function doCampo(texto: string, fuso: string = FUSO): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(texto)) return null;

  const comoUtc = Date.parse(`${texto}:00Z`);
  if (Number.isNaN(comoUtc)) return null;

  let instante = new Date(comoUtc - desvio(new Date(comoUtc), fuso));
  const segundo = desvio(instante, fuso);
  instante = new Date(comoUtc - segundo);
  return instante;
}
